import React from 'react';
import ConfirmAction from '@/components/confirmaction';
import { Alert, Avatar, Button, Drawer, message } from 'antd';
import { PlusOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';
import PageTopbar from '@/components/pagetopbar';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearConversation, dissolveConversation, fetchMembers, hideConversation } from '@/apis/chat';
import { refreshChatSummary } from '@/store/modules/notifications';
import usePrivateAction from '@/utils/usePrivateAction';
import usePrivateList from '@/utils/usePrivateList';
import ConversationMute from '@/components/conversationmute';
import { conversationTitle } from '@/utils/chat';
import './ChatInformation.scss';

const loadSelf = async ({ room_id, user_id }) => {
  if (!room_id) return null;
  const members = await fetchMembers(room_id, { user_id });
  if (!Array.isArray(members)) throw new Error('会话权限响应无效');
  return members.find(member => String(member.user?.id) === String(user_id)) || null;
};

const ChatInformation = ({ room, userId, isMobile = true, open, onClose, onMembers, onMuteChange, onChanged, onRemoved }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { busy, run } = usePrivateAction();
  const members = room.members || [];
  const group = room.kind === 'group';
  const { data: self, loading, error, load } = usePrivateList(loadSelf,
    { room_id: open && group ? room.id : null, user_id: userId }, '群聊权限加载失败，请重试');
  const groupOwner = group && !loading && !error && self?.role === 'owner';
  const canInvite = groupOwner && !room.post && members.length < 100;
  const act = (operation, text, exit = false) => run(operation, () => {
    message.success(text);
    dispatch(refreshChatSummary()).catch(() => {});
    if (exit) { onRemoved?.(room.id); onClose(); navigate('/message'); }
    else onChanged();
  });
  return <Drawer className="chat-information" placement="right" size={isMobile ? '100%' : 480} open={open} onClose={() => !busy && onClose()} closable={false} keyboard={!busy} mask={{ closable: !busy }}
    title={<PageTopbar title={`聊天信息${group ? `（${members.length}）` : ''}`} backLabel="返回聊天" disabled={busy} onBack={onClose} />}>
    <section className="chat-information-members">
      <Button type="text" block className="chat-information-members-heading" disabled={busy} onClick={() => onMembers()}
        aria-label={group ? '查看全部成员与设置' : '查看成员与设置'}>
        <strong>{group ? '群聊成员' : '聊天成员'}</strong>
        <span>查看{members.length}名{group ? '群' : ''}成员 <RightOutlined /></span>
      </Button>
      <div className="chat-information-members-grid">
        {members.slice(0, canInvite ? 14 : 15).map(member => <div className="chat-information-members-item" key={member.user.id}>
          <Avatar size={40} src={member.user.avatar || undefined} icon={<UserOutlined />} />
          <span>{member.user.nickname || member.user.username || '已注销用户'}</span>
        </div>)}
        {canInvite && <Button type="text" className="chat-information-members-invite" disabled={busy} onClick={() => onMembers({ invite: true })}>
          <span className="chat-information-members-invite-icon"><PlusOutlined /></span><span>邀请</span>
        </Button>}
      </div>
    </section>
    <section className="chat-information-settings">
      <div className="chat-information-name">
        <span>{group ? '群聊名称' : '聊天对象'}</span>
        <strong>{conversationTitle(room, userId)}</strong>
      </div>
      {room.post && <Button type="text" block className="chat-information-row" disabled={busy} onClick={() => navigate(`/posts/${room.post}`)}>
        <span>相关帖子</span><RightOutlined />
      </Button>}
    </section>
    <section className="chat-information-settings">
      <ConversationMute room={room} label="消息免打扰" disabled={busy} onChange={onMuteChange} />
      <p className="chat-information-hint">开启后不再提醒此会话的新消息，聊天记录仍会保留。</p>
    </section>
    <section className="chat-information-settings">
      <ConfirmAction title="清空自己可见的聊天记录？无法恢复，其他成员的记录不受影响。" disabled={busy}
        onConfirm={() => act(() => clearConversation(room.id), '聊天记录已清空')}>
        <Button type="text" block className="chat-information-row" disabled={busy}>清空记录</Button>
      </ConfirmAction>
      <ConfirmAction title="隐藏此会话并清空自己的记录？不会退出群聊。" disabled={busy}
        onConfirm={() => act(() => hideConversation(room.id), '会话已隐藏', true)}>
        <Button type="text" block className="chat-information-row" disabled={busy}>隐藏会话</Button>
      </ConfirmAction>
      {groupOwner && <ConfirmAction title="确认解散群聊？所有成员将无法继续访问，无法恢复。" disabled={busy}
        onConfirm={() => act(() => dissolveConversation(room.id), '群聊已解散', true)}>
        <Button type="text" block danger className="chat-information-row" disabled={busy}>解散群聊</Button>
      </ConfirmAction>}
      {group && loading && <p className="chat-information-permission">正在确认群聊权限…</p>}
      {group && error && <Alert type="warning" title={error} action={<Button disabled={busy} onClick={() => load().catch(() => {})}>重试</Button>} />}
    </section>
  </Drawer>;
};

export default ChatInformation;
